import React, { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { NavigationItem } from '../types'
import { ArrowRightOnRectangleIcon, EllipsisHorizontalIcon, XMarkIcon } from '@heroicons/react/24/outline'

interface MobileBottomNavProps {
  items: NavigationItem[]
  /** Incluye el slot de "Cerrar sesión": p. ej. 5 = hasta 4 ítems de nav + salir, o 3 + Más + salir */
  maxVisible?: number
  onLogout?: () => void
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ items, maxVisible = 5, onLogout }) => {
  const location = useLocation()
  const [moreOpen, setMoreOpen] = useState(false)

  const navSlots = onLogout ? maxVisible - 1 : maxVisible

  const { visibleItems, overflowItems } = useMemo(() => {
    if (items.length > navSlots) {
      return {
        visibleItems: items.slice(0, navSlots - 1),
        overflowItems: items.slice(navSlots - 1)
      }
    }
    return { visibleItems: items, overflowItems: [] as NavigationItem[] }
  }, [items, navSlots])

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-[#E5EAF1] bg-white/95 backdrop-blur"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="flex h-16 items-stretch justify-between">
        {visibleItems.map((item) => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== '/' && location.pathname.startsWith(item.href))
          const Icon = item.icon
          return (
            <li key={item.href} className="flex-1">
              <Link
                to={item.href}
                className={`flex h-full flex-col items-center justify-center gap-0.5 px-1 text-[11px] font-medium transition-colors duration-150 ${
                  isActive ? 'text-primary-600' : 'text-slate-500 hover:text-slate-700'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className={`h-[18px] w-[18px] ${isActive ? 'text-primary-600' : ''}`} />
                <span className="max-w-[72px] truncate">{item.name}</span>
              </Link>
            </li>
          )
        })}
        {overflowItems.length > 0 && (
          <li className="flex-1">
            <button
              onClick={() => setMoreOpen(true)}
              className="flex h-full w-full flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-slate-500 hover:text-slate-700"
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
            >
              <EllipsisHorizontalIcon className="h-5 w-5" />
              <span>Más</span>
            </button>
          </li>
        )}
        {onLogout && (
          <li className="flex-1">
            <button
              type="button"
              onClick={onLogout}
              className="flex h-full w-full flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-slate-500 hover:text-rose-600"
              title="Cerrar sesión"
            >
              <ArrowRightOnRectangleIcon className="h-[18px] w-[18px]" />
              <span className="max-w-[72px] truncate">Salir</span>
            </button>
          </li>
        )}
      </ul>

      {moreOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMoreOpen(false)} />
          <div
            className="absolute bottom-0 left-0 right-0 rounded-t-2xl bg-white p-4 shadow-xl"
            style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
          >
            <div className="mb-2 flex flex-col items-center">
              <div className="mb-2 h-1 w-10 rounded-full bg-slate-300" />
            </div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Más módulos</h3>
              <button
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                onClick={() => setMoreOpen(false)}
                aria-label="Cerrar"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {overflowItems.map((item) => {
                const isActive = location.pathname === item.href
                const Icon = item.icon
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMoreOpen(false)}
                    className={`flex flex-col items-center justify-center rounded-xl border p-3 text-xs ${
                      isActive
                        ? 'border-primary-200 bg-primary-50 text-primary-700'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className={`mb-1 h-5 w-5 ${isActive ? 'text-primary-600' : ''}`} />
                    <span className="max-w-[72px] truncate">{item.name}</span>
                  </Link>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}

export default MobileBottomNav
